import { Home, PlusSquare, MessageSquare, User, Newspaper, Bell } from 'lucide-react';
import { cn } from '@/lib/utils';

interface BottomNavProps<T extends string> {
  activeTab: T;
  onTabChange: (tab: T) => void;
  availableTabs: readonly T[]; // Required: which tabs to show
  badgeCounts?: {
    messages?: number;
    notifications?: number;
  };
}

const ALL_TABS = [
  { id: 'home', icon: Home, label: 'Home' },
  { id: 'post', icon: PlusSquare, label: 'Post' },
  { id: 'feeds', icon: Newspaper, label: 'Feeds' },
  { id: 'messages', icon: MessageSquare, label: 'Messages' },
  { id: 'profile', icon: User, label: 'Profile' },
] as const;

export function BottomNav<T extends string>({ 
  activeTab, 
  onTabChange,
  availableTabs,
  badgeCounts = {}
}: BottomNavProps<T>) {
  const tabs = ALL_TABS.filter(t => availableTabs.includes(t.id as T));

  return (
    <nav className="fixed bottom-0 left-0 right-0 bg-background border-t border-border pb-safe z-40">
      <div className="container max-w-lg mx-auto px-4">
        <div className="flex justify-around items-center h-16">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            
            // Get badge count for this tab
            const badgeCount = tab.id === 'messages' ? badgeCounts.messages : 
                              tab.id === 'feeds' ? badgeCounts.notifications : 0;
            
            return (
              <button
                key={tab.id}
                onClick={() => onTabChange(tab.id as T)}
                className={cn(
                  "flex flex-col items-center justify-center w-full h-full space-y-1 transition-colors relative",
                  isActive ? "text-primary" : "text-muted-foreground hover:text-foreground"
                )}
              >
                <div className="relative">
                  <Icon className={cn("w-6 h-6", isActive && "fill-current")} />
                  {badgeCount !== undefined && badgeCount > 0 && (
                    <span className="absolute -top-1 -right-2 min-w-[16px] h-[16px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-0.5">
                      {badgeCount > 9 ? '9+' : badgeCount}
                    </span>
                  )}
                </div>
                <span className="text-xs font-medium">{tab.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    </nav>
  );
}

export default BottomNav;
